/**
 * Förhandlingsframställan enligt hyresförhandlingslagen (1978:304) — the
 * family ported from the hand-written React template of the same name.
 *
 * Each block is a pure `compile(input, context) → nodes`. All the branching
 * that used to live inside JSX lives here, and none of it survives into the
 * output: the compiled section says what the document says, and nothing
 * about which of the thirty branches produced it. That is what lets us
 * rewrite — or delete — these compilers without touching a document that
 * was already issued.
 *
 * The legal text is carried verbatim from the Haymanot-approved source
 * (`base-templates/Mall - förhandlingsframställning.docx`). DO NOT
 * paraphrase or reorder: a wording change is a NEW block version.
 */
import { attachmentRef, details, em, group, h, nodes, option, optionList, p, pageBreak, section, strong, t, table, val, } from '@hyranai/regta-document-ir';
import { multiSelectSlot, printLabel, rows, str } from '../lib.js';
import { dateVal, fieldRows, partySlot, pick, pickCode, picks, propertyListSlot, selectSlot, shown, signingBlockDefinition, textVal, ticked, whenIncludes, whenOption, } from '../common/index.js';
import { optionByCode } from '../forhandlingsordning/family.js';
import { ENCLOSURE_OPTIONS, GROUNDS_OPTIONS, NYBYGGNATION_RENT_TYPE_OPTIONS, OTHER_TERMS_OPTIONS, SCOPES_REQUIRING_GROUNDS, SCOPE_EXTENT_OPTIONS, SCOPE_OPTIONS, } from './options.js';
const GROUND_DETAIL_LABELS = {
    grounds_investeringar: 'Genomförda investeringar',
    grounds_bruksvarde: 'Förändrat bruksvärde',
    grounds_jamforelse: 'Jämförelse med likvärdiga lägenheter i bruksvärdeshänseende',
    grounds_annat: 'Annat (grund)',
};
const LEGAL = {
    plane: 'legal',
    status: 'published',
    language: 'sv-SE',
    legalReview: { reviewedBy: 'Haymanot', source: 'base-templates/Mall - förhandlingsframställning.docx' },
};
/**
 * An option line whose free-text detail prints on the same line
 * ("Annat: ______"). The blank rule is what makes one compiled document
 * serve both a printed empty form and a filled one.
 */
function optionWithFreeText(checked, label, text, sourceKey) {
    return option(checked, [t(`${label}: `), val(checked ? text : null, { blankWidth: 'long', sourceKey })]);
}
const codesOf = (options, raw) => picks(options, raw).map((o) => o.code);
const scopeOf = (input) => codesOf(SCOPE_OPTIONS, input.negotiation_scope);
const SCOPE = (code) => optionByCode(SCOPE_OPTIONS, code);
/* ------------------------------------------------------------------ */
export const mottagare = {
    ...LEGAL,
    blockKey: 'framstallan.mottagare',
    version: 'v1',
    title: 'Till',
    summary: 'Date line plus the recipient tenant organisation panel.',
    slots: [
        { key: 'request_date', label: 'Datum', type: 'date', required: true, statutory: true },
        partySlot('tenant_org_name', 'Hyresgästorganisation', 'other', 0, 'name', { required: true, statutory: true }),
        partySlot('tenant_org_address', 'Adress', 'other', 0, 'address'),
        partySlot('tenant_org_email', 'E-post', 'other', 0, 'email'),
    ],
    compile: (input, ctx) => nodes(h(1, 'Förhandlingsframställning', { align: 'center' }), p([em('enligt hyresförhandlingslagen (1978:304)')], { align: 'center', variant: 'fine_print' }), fieldRows([['Datum', [dateVal(ctx, input, 'request_date')]]]), group(nodes(fieldRows([
        ['Hyresgästorganisation', [textVal(input, 'tenant_org_name')]],
        ['Adress', [textVal(input, 'tenant_org_address')]],
        ['E-post', [textVal(input, 'tenant_org_email')]],
    ])), { variant: 'boxed', title: 'Till' })),
};
export const begaran = {
    ...LEGAL,
    blockKey: 'framstallan.begaran',
    version: 'v1',
    title: 'Begäran om förhandling',
    summary: 'The request itself: which properties, and whether it covers the whole property or listed apartments.',
    slots: [
        propertyListSlot('property_summary', 'Fastigheter', 'summary', {
            required: true,
            statutory: true,
            help: 'En fastighet per rad. Hämtas automatiskt från länkade fastigheter.',
        }),
        selectSlot('scope_extent', 'Framställan avser', SCOPE_EXTENT_OPTIONS, { required: true, default: 'whole_property' }),
        {
            key: 'scope_apartments',
            label: 'Lägenheter',
            type: 'textarea',
            help: 'Ange vilka lägenheter framställan avser.',
            visibleWhen: whenOption('scope_extent', optionByCode(SCOPE_EXTENT_OPTIONS, 'specific_apartments')),
        },
    ],
    compile: (input, ctx) => {
        const apartments = pickCode(SCOPE_EXTENT_OPTIONS, input.scope_extent) === 'specific_apartments';
        return [
            section('begaran', 'Begäran om förhandling', [
                p('Hyresvärden begär härmed förhandling enligt hyresförhandlingslagen beträffande följande fastighet(er):'),
                group(nodes(fieldRows([['Fastigheter', [textVal(input, 'property_summary')]]])), { variant: 'boxed' }),
                p('Framställan avser', { variant: 'caption' }),
                optionList([
                    option(ticked(ctx, !apartments), 'Hela fastigheten', { code: 'whole_property' }),
                    option(ticked(ctx, apartments), [
                        t('Följande lägenheter: '),
                        val(ticked(ctx, apartments) ? str(input.scope_apartments) : null, { blankWidth: 'long', sourceKey: 'scope_apartments' }),
                    ], { code: 'specific_apartments' }),
                ], { sourceKey: 'scope_extent', select: 'single' }),
            ]),
        ];
    },
};
export const forhandlingenAvser = {
    ...LEGAL,
    blockKey: 'framstallan.forhandlingen-avser',
    version: 'v1',
    title: 'Förhandlingen avser',
    summary: 'Checklist of what the negotiation concerns; drives whether grounds must be stated.',
    slots: [
        multiSelectSlot('negotiation_scope', 'Förhandlingen avser', SCOPE_OPTIONS, { required: true, statutory: true }),
        {
            key: 'negotiation_scope_annat',
            label: 'Annat (förhandlingen avser)',
            type: 'text',
            visibleWhen: whenIncludes('negotiation_scope', SCOPE('annat')),
        },
    ],
    compile: (input, ctx) => {
        const scope = scopeOf(input);
        return [
            section('forhandlingen-avser', 'Förhandlingen avser', [
                optionList(SCOPE_OPTIONS.map((o) => o.code === 'annat'
                    ? {
                        ...optionWithFreeText(ticked(ctx, scope.includes('annat')), 'Annat', str(input.negotiation_scope_annat), 'negotiation_scope_annat'),
                        code: o.code,
                    }
                    : option(ticked(ctx, scope.includes(o.code)), printLabel(o), { code: o.code })), { sourceKey: 'negotiation_scope' }),
            ]),
        ];
    },
};
export const yrkande = {
    ...LEGAL,
    blockKey: 'framstallan.yrkande',
    version: 'v1',
    title: 'Yrkande',
    summary: 'The operative claim. Either the written yrkanden per selected scope, or a statement that it is presented at the meeting.',
    slots: [
        {
            key: 'yrkande_vid_sammantrade',
            label: 'Hyresvärden presenterar sitt yrkande vid förhandlingssammanträdet',
            type: 'boolean',
            default: false,
            help: 'Om ikryssad anges inget skriftligt yrkande här — yrkandet presenteras ' +
                'vid sammanträdet. Fritextfälten nedan låses; endast ikraftträdandedatum fylls i.',
        },
        {
            key: 'yrkande_rent_change',
            label: 'Yrkande — ändring av hyror',
            type: 'textarea',
            help: 'Att hyrorna ändras enligt följande (t.ex. "med 5 procent").',
            visibleWhen: whenIncludes('negotiation_scope', SCOPE('general_development'), SCOPE('rent_change')),
        },
        multiSelectSlot('yrkande_andra_villkor_alts', 'Yrkande — andra hyresvillkor', OTHER_TERMS_OPTIONS, {
            help: 'Välj de villkor yrkandet avser. Använd "Annat" för övriga.',
            visibleWhen: whenIncludes('negotiation_scope', SCOPE('other_terms')),
        }),
        {
            key: 'yrkande_andra_villkor_annat',
            label: 'Andra hyresvillkor — annat',
            type: 'textarea',
            visibleWhen: {
                all: [
                    whenIncludes('negotiation_scope', SCOPE('other_terms')),
                    whenIncludes('yrkande_andra_villkor_alts', optionByCode(OTHER_TERMS_OPTIONS, 'annat')),
                ],
            },
        },
        {
            key: 'yrkande_ombyggnad',
            label: 'Yrkande — första hyra efter ombyggnad',
            type: 'textarea',
            help: 'Att hyran bestäms till …',
            visibleWhen: whenIncludes('negotiation_scope', SCOPE('first_rent_renovation')),
        },
        {
            key: 'yrkande_nybyggnation',
            label: 'Yrkande — nybyggnation',
            type: 'textarea',
            help: 'Att hyran bestäms till …',
            visibleWhen: whenIncludes('negotiation_scope', SCOPE('new_construction')),
        },
        selectSlot('nybyggnation_hyrestyp', 'Hyran ska vara', NYBYGGNATION_RENT_TYPE_OPTIONS, {
            help: 'Nybyggnation i ett befintligt hus är endast möjlig om det rör sig om nybyggda ytor / ' +
                'lägenheter som tillkommit efter att ytor som tidigare användes för annat konverterats ' +
                'till bostäder.',
            visibleWhen: whenIncludes('negotiation_scope', SCOPE('new_construction')),
        }),
        {
            key: 'yrkande_annat',
            label: 'Yrkande — annat',
            type: 'textarea',
            help: 'Fritt formulerat yrkande för det som valts under "Annat" i Förhandlingen avser.',
            visibleWhen: whenIncludes('negotiation_scope', SCOPE('annat')),
        },
        { key: 'effective_date', label: 'Föreslås träda i kraft från och med', type: 'date', required: true, statutory: true },
    ],
    compile: (input, ctx) => {
        const scope = scopeOf(input);
        const visible = (...codes) => shown(ctx, codes.some((code) => scope.includes(code)));
        // Not blank-gated: on a blank form the box is unticked, so every yrkande-val prints.
        const presenterar = input.yrkande_vid_sammantrade === true;
        const otherTerms = codesOf(OTHER_TERMS_OPTIONS, input.yrkande_andra_villkor_alts);
        const written = nodes(visible('general_development', 'rent_change')
            ? p([textVal(input, 'yrkande_rent_change')])
            : null, visible('other_terms')
            ? optionList(OTHER_TERMS_OPTIONS.map((o) => o.code === 'annat'
                ? {
                    ...optionWithFreeText(ticked(ctx, otherTerms.includes('annat')), 'Annat', str(input.yrkande_andra_villkor_annat), 'yrkande_andra_villkor_annat'),
                    code: o.code,
                }
                : option(ticked(ctx, otherTerms.includes(o.code)), printLabel(o), { code: o.code })), { sourceKey: 'yrkande_andra_villkor_alts' })
            : null, visible('first_rent_renovation')
            ? p([
                t('Första hyra efter ombyggnad/standardhöjande åtgärder: '),
                textVal(input, 'yrkande_ombyggnad'),
            ])
            : null, visible('new_construction') ? nybyggnationLine(input, ctx) : null, visible('annat') ? p([textVal(input, 'yrkande_annat')]) : null);
        return [
            section('yrkande', 'Yrkande', [
                p([strong('Hyresvärden yrkar:')]),
                ...(presenterar ? [p('Hyresvärden presenterar sitt yrkande vid förhandlingssammanträdet.')] : written),
                p([
                    t('Den begärda ändringen föreslås träda i kraft från och med: '),
                    dateVal(ctx, input, 'effective_date'),
                ]),
            ]),
        ];
    },
};
function nybyggnationLine(input, ctx) {
    const rentType = pick(NYBYGGNATION_RENT_TYPE_OPTIONS, input.nybyggnation_hyrestyp);
    const parts = [t('Nybyggnation: '), textVal(input, 'yrkande_nybyggnation')];
    if (ctx.mode !== 'blank' && rentType) {
        parts.push(t(' Hyran ska vara '), strong(printLabel(rentType)), t('.'));
    }
    else if (ctx.mode === 'blank' || str(input.nybyggnation_hyrestyp)) {
        parts.push(t(' Hyran ska vara '), val(null, { blankWidth: 'medium', sourceKey: 'nybyggnation_hyrestyp' }), t('.'));
    }
    return p(parts);
}
export const grund = {
    ...LEGAL,
    blockKey: 'framstallan.grund',
    version: 'v1',
    title: 'Grund för yrkandet',
    summary: 'The grounds relied on, with an optional free-text elaboration per ground.',
    slots: [
        multiSelectSlot('grounds', 'Grund för yrkandet', GROUNDS_OPTIONS, {
            help: 'Vid ändring av hyresvillkor utöver den ekonomiska utvecklingen på orten.',
        }),
        ...GROUNDS_OPTIONS.filter((o) => o.detailKey).map((o) => ({
            key: o.detailKey,
            label: GROUND_DETAIL_LABELS[o.detailKey] ?? o.label,
            type: 'textarea',
            visibleWhen: whenIncludes('grounds', o),
        })),
    ],
    /**
     * The section prints only when a grund is ticked (always on a blank form),
     * as in the legacy template. A framställan beyond the ordinary yearly
     * adjustment must state one; that is a diagnostic, not a printed section.
     */
    compile: (input, ctx) => {
        const scope = scopeOf(input);
        const grounds = codesOf(GROUNDS_OPTIONS, input.grounds);
        const required = scope.some((code) => SCOPES_REQUIRING_GROUNDS.includes(code));
        const missing = required && ctx.mode !== 'blank' && grounds.length === 0
            ? [
                {
                    severity: 'error',
                    code: 'grounds_required',
                    message: 'Förhandlingen avser mer än den ekonomiska utvecklingen på orten — en grund måste anges.',
                    slotKey: 'grounds',
                },
            ]
            : [];
        if (ctx.mode !== 'blank' && grounds.length === 0)
            return { nodes: [], diagnostics: missing };
        const detailFor = (option) => {
            if (!option.detailKey || !ticked(ctx, grounds.includes(option.code)))
                return [];
            return str(input[option.detailKey]) ? [p([textVal(input, option.detailKey)])] : [];
        };
        return {
            nodes: [
                section('grund', 'Grund för yrkandet', [
                    p('Som grund för yrkandet åberopas:'),
                    optionList(GROUNDS_OPTIONS.map((o) => ({
                        ...option(ticked(ctx, grounds.includes(o.code)), printLabel(o), { code: o.code }),
                        children: detailFor(o),
                    })), { sourceKey: 'grounds' }),
                ]),
            ],
            diagnostics: missing,
        };
    },
};
export const underlag = {
    ...LEGAL,
    blockKey: 'framstallan.underlag',
    version: 'v1',
    title: 'Underlag',
    summary: 'The enclosures. Each ticked enclosure is declared as an attachment and numbered by the assembler.',
    slots: [
        multiSelectSlot('enclosures', 'Följande handlingar bifogas', ENCLOSURE_OPTIONS, {
            help: 'Ladda upp handlingarna under fliken Bilagor (valfritt antal).',
        }),
    ],
    compile: (input, ctx) => {
        const enclosures = codesOf(ENCLOSURE_OPTIONS, input.enclosures);
        const selected = ENCLOSURE_OPTIONS.filter((o) => enclosures.includes(o.code));
        return {
            nodes: [
                section('underlag', 'Underlag', [
                    p('Följande handlingar bifogas:'),
                    optionList(ENCLOSURE_OPTIONS.map((o) => option(ticked(ctx, enclosures.includes(o.code)), o.label, { code: o.code })), { sourceKey: 'enclosures' }),
                    p([em('Eventuella ytterligare bilagor bifogas separat.')], { variant: 'fine_print' }),
                ]),
            ],
            /**
             * Declared, not printed (the legacy form prints no bilaga list here):
             * the block says which bilagor exist, the assembler numbers them, and
             * a cross-reference can cite one.
             */
            attachments: ctx.mode === 'blank' ? [] : selected.map((o) => ({ id: o.code, label: o.label, pending: true })),
        };
    },
};
export const sammantrade = {
    ...LEGAL,
    blockKey: 'framstallan.sammantrade',
    version: 'v1',
    title: 'Första förhandlingssammanträde',
    summary: 'The 15 § summons: at least two proposed times, all within three weeks, plus a deadline for counter-proposals.',
    slots: [
        {
            key: 'meeting_proposals',
            label: 'Förslag på tid och plats',
            type: 'table',
            required: true,
            statutory: true,
            help: 'Minst två förslag, samtliga inom tre veckor (15 § hyresförhandlingslagen).',
            repeats: true,
            columns: [
                { key: 'when', label: 'Datum och tid', type: 'text' },
                { key: 'place', label: 'Plats', type: 'text' },
            ],
        },
        {
            key: 'meeting_digital',
            label: 'Digitalt möte godtas via',
            type: 'text',
            help: 'T.ex. länk eller tjänst. Lämnas tomt om digitalt möte inte erbjuds.',
        },
        {
            key: 'alt_proposal_deadline',
            label: 'Senast för förslag på alternativ tid',
            type: 'date',
            help: 'Om föreslagen tid eller plats inte kan accepteras ska motparten föreslå alternativ tidpunkt senast detta datum. Bör ligga inom tre veckor.',
        },
    ],
    compile: (input, ctx) => {
        const proposals = rows(input.meeting_proposals);
        // A blank printable form gets two empty rows to write on; 15 § requires
        // at least two proposals, so two is the floor, not a guess.
        const display = proposals.length > 0 ? proposals : ctx.mode === 'blank' ? [{}, {}] : [];
        return {
            nodes: [
                section('sammantrade', 'Första förhandlingssammanträde', [
                    p('Med stöd av 15 § hyresförhandlingslagen kallas härmed till första förhandlingssammanträde.'),
                    group([
                        details(display.flatMap((row, index) => [
                            [`Förslag ${index + 1} — datum och tid`, [p([val(str(row.when), { blankWidth: 'medium' })])]],
                            [`Förslag ${index + 1} — plats`, [p([val(str(row.place), { blankWidth: 'medium' })])]],
                        ])),
                    ], { variant: 'boxed' }),
                    ...(shown(ctx, Boolean(str(input.meeting_digital)))
                        ? [
                            p([
                                t('Hyresvärden godtar digitalt möte som alternativ via: '),
                                textVal(input, 'meeting_digital'),
                            ]),
                        ]
                        : []),
                    p([
                        t('Om föreslagen tid eller plats inte kan accepteras ombeds motparten snarast, dock senast '),
                        dateVal(ctx, input, 'alt_proposal_deadline'),
                        t(', föreslå alternativ tidpunkt.'),
                    ]),
                ]),
            ],
            diagnostics: ctx.mode !== 'blank' && proposals.length < 2
                ? [
                    {
                        severity: 'error',
                        code: 'insufficient_meeting_proposals',
                        message: '15 § hyresförhandlingslagen kräver minst två föreslagna tider.',
                        slotKey: 'meeting_proposals',
                    },
                ]
                : [],
        };
    },
};
export const kontaktperson = {
    ...LEGAL,
    blockKey: 'framstallan.kontaktperson',
    version: 'v1',
    title: 'Kontaktperson',
    summary: 'The landlord-side contact for the negotiation.',
    slots: [
        { key: 'contact_name', label: 'Namn', type: 'text', required: true },
        { key: 'contact_phone', label: 'Telefon', type: 'text' },
        { key: 'contact_email', label: 'E-post', type: 'text' },
    ],
    compile: (input) => [
        section('kontaktperson', 'Kontaktperson', [
            group(nodes(fieldRows([
                ['Namn', [textVal(input, 'contact_name')]],
                ['Telefon', [textVal(input, 'contact_phone')]],
                ['E-post', [textVal(input, 'contact_email')]],
            ])), { variant: 'boxed' }),
        ]),
    ],
};
/**
 * Landlord signature as its own numbered section. Only the landlord signs a
 * framställan; the organisation is the recipient. The panel itself is the
 * family signing block (no execution statement, Ort/Datum per the host's
 * signing context); the composition hides its "Underskrifter" caption
 * (`heading: false`) so the section heading is the only one.
 */
export const underskrift = signingBlockDefinition({
    blockKey: 'framstallan.underskrift',
    title: 'Underskrift',
    summary: 'Landlord signature panel. Only the landlord signs a framställan; the organisation is the recipient.',
    legalReview: LEGAL.legalReview,
    defaults: { ref: 'underskrift', title: 'Underskrift', parties: [{ label: 'Hyresvärd', role: 'lessor', ordinal: 0 }] },
});
/** From `base-templates/Mall för hyreslista[ med servicetillägg].xlsx`. */
const HYRESLISTA_COLUMNS = [
    { key: 'fastighetsagare', label: 'Fastighetsägare' },
    { key: 'fastighet', label: 'Fastighet' },
    { key: 'gatuadress', label: 'Gatuadress' },
    { key: 'lagenhetsnummer', label: 'Lägenhetsnummer' },
    { key: 'lantmateri_lgh_nr', label: 'Lantmäteriets lgh-nr' },
    { key: 'lagenhetstyp', label: 'Lägenhetstyp' },
    { key: 'yta', label: 'Lägenhets-yta', kind: 'number' },
    { key: 'arshyra', label: 'Årshyra', kind: 'currency' },
    { key: 'servicetillagg', label: 'Servicetillägg i årshyran', kind: 'currency', serviceOnly: true },
    { key: 'arshyra_slutkund', label: 'Årshyra slutkund', kind: 'currency', sumOf: ['arshyra', 'servicetillagg'], serviceOnly: true },
];
function toNum(value) {
    if (typeof value === 'number')
        return Number.isFinite(value) ? value : null;
    if (typeof value === 'string') {
        const cleaned = value.replace(/\s/g, '').replace(',', '.').replace(/[^0-9.-]/g, '');
        if (cleaned === '' || cleaned === '-')
            return null;
        const n = Number.parseFloat(cleaned);
        return Number.isFinite(n) ? n : null;
    }
    return null;
}
function hyreslistaCell(column, row) {
    let value = row[column.key];
    if (column.sumOf) {
        const nums = column.sumOf.map((k) => toNum(row[k]));
        value = nums.every((n) => n == null) ? null : nums.reduce((acc, n) => acc + (n ?? 0), 0);
    }
    if (value == null || value === '')
        return null;
    if (column.kind) {
        const n = toNum(value);
        return n == null ? String(value) : n.toLocaleString('sv-SE');
    }
    return String(value);
}
/**
 * "Bilaga – Hyreslista": part of the main document, after the signature,
 * on a new page. Printed when "Hyreslista" is ticked under Underlag (and on
 * a blank form).
 */
export const hyreslista = {
    ...LEGAL,
    blockKey: 'framstallan.hyreslista',
    version: 'v1',
    title: 'Bilaga – Hyreslista',
    summary: 'The rent roll appendix, printed after the signature when a hyreslista is enclosed.',
    slots: [
        {
            key: 'hyreslista_rows',
            label: 'Hyreslista',
            type: 'table',
            repeats: true,
            help: 'En rad per lägenhet. "Årshyra slutkund" räknas ut automatiskt. Bifogas sist i dokumentet.',
            visibleWhen: whenIncludes('enclosures', optionByCode(ENCLOSURE_OPTIONS, 'rent_roll')),
            columns: HYRESLISTA_COLUMNS.filter((c) => !c.sumOf).map((c) => ({
                key: c.key,
                label: c.label,
                type: c.kind === 'currency' ? 'currency' : c.kind === 'number' ? 'number' : 'text',
            })),
        },
    ],
    compile: (input, ctx) => {
        const enclosed = codesOf(ENCLOSURE_OPTIONS, input.enclosures).includes('rent_roll');
        if (!shown(ctx, enclosed))
            return null;
        const service = input.hyreslista_variant === 'service';
        const columns = HYRESLISTA_COLUMNS.filter((c) => service || !c.serviceOnly);
        const entries = rows(input.hyreslista_rows);
        const headers = columns.map((c) => ({
            key: c.key,
            header: [t(c.label)],
            ...(c.kind ? { align: 'right' } : {}),
        }));
        const body = entries.length === 0
            ? [{ cells: [{ content: [p('(Inga rader ifyllda ännu)', { align: 'center', variant: 'caption' })], colSpan: columns.length }] }]
            : entries.map((row) => ({
                cells: columns.map((c) => ({ content: [p([val(hyreslistaCell(c, row), { blankWidth: 'short' })])] })),
            }));
        return [
            pageBreak(),
            p([strong('Bilaga – Hyreslista')]),
            table(headers, body, { variant: 'data', repeatTableHeader: true }),
        ];
    },
};
/**
 * A tenant-plane free-text section (ADR-0060's custom sections, as a
 * block). It carries no legal text of its own, which is exactly why it may
 * be authored outside legal review.
 */
export const bilagaHanvisning = {
    blockKey: 'framstallan.bilaga-hanvisning',
    version: 'v1',
    plane: 'shared',
    status: 'published',
    language: 'sv-SE',
    title: 'Hänvisning till bilaga',
    summary: 'A sentence pointing at one of the document’s declared attachments, by reference rather than by number.',
    slots: [
        { key: 'lead_in', label: 'Inledande text', type: 'text', required: true },
        { key: 'attachment_id', label: 'Bilaga', type: 'attachment', required: true },
    ],
    compile: (input) => {
        const target = str(input.attachment_id);
        if (!target)
            return null;
        return [p([t(`${str(input.lead_in) ?? 'Se'} `), attachmentRef(target), t('.')])];
    },
};
export const FRAMSTALLAN_BLOCKS = [
    mottagare,
    begaran,
    forhandlingenAvser,
    yrkande,
    grund,
    underlag,
    sammantrade,
    kontaktperson,
    underskrift,
    hyreslista,
    bilagaHanvisning,
];
//# sourceMappingURL=blocks.js.map